import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { Menu, X, Search, ChevronDown, User, LogOut, GraduationCap, Bell } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { label: '首页', href: '/' },
  { label: '学校概况', children: ['学校简介', '现任领导', '历史沿革', '长大标识', '长大映像'] },
  { label: '教育教学', children: ['本科教育', '研究生教育', '国际教育', '继续教育', '重点学科'] },
  { label: '科学研究', children: ['科研概况', '科研平台', '科研团队', '科研成果', '学术期刊'] },
  { label: '招生就业', children: ['本科招生', '研究生招生', '留学生招生', '学生就业'] },
  { label: '图书馆', href: '/' },
  { label: '校友会', href: '/' },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollY } = useScroll();

  useEffect(() => {
    const unsub = scrollY.on('change', v => setScrolled(v > 20));
    return unsub;
  }, [scrollY]);

  const handleSignOut = async () => {
    await signOut();
    toast.success('已成功退出登录');
    navigate('/');
  };

  const initials = profile?.full_name
    ? profile.full_name.slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? 'U';

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* @section: top-bar */}
      <motion.div
        initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}
        className="hidden md:flex items-center justify-between px-6 py-1.5 text-xs"
        style={{ background: 'oklch(0.18 0.10 240)', color: 'oklch(0.78 0.02 240)' }}>
        <div className="flex items-center gap-4">
          <span className="font-medium">长安大学 Chang'an University</span>
          <span className="opacity-30">|</span>
          {['办事大厅', '校园地图', 'English'].map(t => (
            <motion.a key={t} href="#" whileHover={{ color: '#ffffff' }} style={{ color: 'inherit' }}>{t}</motion.a>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {['教务系统', '校园邮件', '图书馆', 'VPN服务'].map(t => (
            <motion.a key={t} href="#" whileHover={{ color: '#ffffff' }} style={{ color: 'inherit' }}>{t}</motion.a>
          ))}
        </div>
      </motion.div>

      {/* @section: main-header */}
      <header
        className="sticky top-0 z-50 border-b transition-all duration-300"
        style={{
          backdropFilter: 'blur(20px)',
          borderColor: scrolled ? 'rgba(0,48,135,0.12)' : 'transparent',
          background: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.75)',
          boxShadow: scrolled ? '0 4px 30px rgba(0,48,135,0.08)' : 'none',
        }}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <motion.div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#001a4d,#003087)' }}
              whileHover={{ scale: 1.1, rotate: 5, boxShadow: '0 4px 20px rgba(0,48,135,0.4)' }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}>
              <GraduationCap className="w-5 h-5 text-white" />
            </motion.div>
            <div className="hidden sm:block">
              <div className="text-base font-bold leading-tight" style={{ color: 'oklch(0.20 0.12 240)', fontFamily: "'Noto Serif SC', serif" }}>长安大学</div>
              <div className="text-[10px] tracking-widest opacity-50 text-foreground">CHANG'AN UNIVERSITY</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_ITEMS.map((item, idx) => (
              <div key={item.label} className="relative"
                onMouseEnter={() => item.children && setActiveMenu(item.label)}
                onMouseLeave={() => setActiveMenu(null)}>
                {item.href ? (
                  <Link to={item.href}>
                    <motion.div
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer relative"
                      whileHover={{ color: '#003087', backgroundColor: 'rgba(0,48,135,0.06)' }}
                      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                      style={{ color: location.pathname === item.href ? '#003087' : 'inherit' }}>
                      {location.pathname === item.href && (
                        <motion.div layoutId="nav-active" className="absolute inset-0 rounded-lg" style={{ background: 'rgba(0,48,135,0.08)' }} />
                      )}
                      <span className="relative z-10">{item.label}</span>
                    </motion.div>
                  </Link>
                ) : (
                  <motion.button
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg text-foreground"
                    whileHover={{ color: '#003087', backgroundColor: 'rgba(0,48,135,0.06)' }}
                    initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                    {item.label}
                    <motion.span animate={{ rotate: activeMenu === item.label ? 180 : 0 }} transition={{ type: 'spring', stiffness: 300, damping: 25 }}>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </motion.span>
                  </motion.button>
                )}
                <AnimatePresence>
                  {item.children && activeMenu === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                      className="absolute top-full left-0 mt-2 w-44 rounded-2xl py-1.5 z-50 border"
                      style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderColor: 'rgba(0,48,135,0.1)', boxShadow: '0 8px 40px rgba(0,48,135,0.12)' }}>
                      {item.children.map((child, ci) => (
                        <motion.a key={child} href="#"
                          className="block px-4 py-2 text-sm text-foreground rounded-xl mx-1"
                          initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: ci * 0.04 }}
                          whileHover={{ x: 4, color: '#003087', backgroundColor: 'rgba(0,48,135,0.06)' }}>
                          {child}
                        </motion.a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-1.5">
            <motion.button className="w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground"
              whileHover={{ backgroundColor: 'rgba(0,48,135,0.08)', color: '#003087', scale: 1.08 }}
              whileTap={{ scale: 0.92 }}>
              <Search className="w-4 h-4" />
            </motion.button>

            {user ? (
              <div className="flex items-center gap-1.5">
                <motion.button className="w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground relative"
                  whileHover={{ backgroundColor: 'rgba(0,48,135,0.08)', color: '#003087', scale: 1.08 }}
                  whileTap={{ scale: 0.92 }}>
                  <Bell className="w-4 h-4" />
                  <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 2 }}
                    className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 border border-white" />
                </motion.button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <motion.button className="flex items-center gap-2 px-2 py-1.5 rounded-xl"
                      whileHover={{ backgroundColor: 'rgba(0,48,135,0.06)', scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}>
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={profile?.avatar_url ?? undefined} />
                        <AvatarFallback className="text-xs text-white" style={{ background: 'linear-gradient(135deg,#001a4d,#003087)' }}>{initials}</AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block text-sm font-medium max-w-[80px] truncate">
                        {profile?.full_name ?? user.email?.split('@')[0]}
                      </span>
                    </motion.button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 rounded-2xl" style={{ boxShadow: '0 8px 40px rgba(0,48,135,0.12)' }}>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="rounded-xl cursor-pointer">
                      <User className="w-4 h-4 mr-2" />个人中心
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive rounded-xl cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />退出登录
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <motion.button
                onClick={() => navigate('/auth')}
                className="px-4 py-2 rounded-xl text-white text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg,#001a4d,#003087)', boxShadow: '0 2px 16px rgba(0,48,135,0.3)' }}
                whileHover={{ scale: 1.05, boxShadow: '0 4px 24px rgba(0,48,135,0.5)' }}
                whileTap={{ scale: 0.96 }}>
                登录
              </motion.button>
            )}

            <motion.button className="lg:hidden w-9 h-9 rounded-xl flex items-center justify-center"
              whileHover={{ backgroundColor: 'rgba(0,48,135,0.08)' }} whileTap={{ scale: 0.92 }}
              onClick={() => setMobileOpen(!mobileOpen)}>
              <AnimatePresence mode="wait">
                {mobileOpen
                  ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}><X className="w-5 h-5" /></motion.div>
                  : <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}><Menu className="w-5 h-5" /></motion.div>
                }
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="lg:hidden border-t overflow-hidden"
              style={{ borderColor: 'rgba(0,48,135,0.1)', background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)' }}>
              <motion.div className="px-4 py-3 space-y-0.5"
                variants={{ visible: { transition: { staggerChildren: 0.05 } } }} initial="hidden" animate="visible">
                {NAV_ITEMS.map(item => (
                  <motion.div key={item.label} variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0 } }}>
                    {item.href
                      ? <Link to={item.href} className="block px-3 py-2.5 text-sm rounded-xl font-medium hover:text-primary hover:bg-primary/5 transition-all" onClick={() => setMobileOpen(false)}>{item.label}</Link>
                      : <div className="px-3 py-2.5 text-sm rounded-xl font-medium text-foreground">{item.label}</div>
                    }
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Page content */}
      <motion.main className="flex-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.1 }}>
        {children}
      </motion.main>

      {/* @section: footer */}
      <footer style={{ background: 'linear-gradient(135deg,#050d1a,#001230)', color: 'oklch(0.70 0.02 240)' }} className="mt-auto">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,rgba(59,130,246,0.4),rgba(0,48,135,0.6))' }}>
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-white text-sm" style={{ fontFamily: "'Noto Serif SC', serif" }}>长安大学</span>
              </div>
              <p className="text-xs leading-relaxed opacity-50">西安市南二环西段<br />长安大学 710064<br />Tel: 029-82334114</p>
            </div>
            {[
              { title: '教育教学', links: ['本科生教育', '研究生教育', '国际教育', '继续教育'] },
              { title: '科学研究', links: ['科研概况', '科研平台', '学术成果', '学术期刊'] },
              { title: '服务支持', links: ['教务系统', '图书馆', '校园邮件', '网络服务'] },
            ].map(col => (
              <div key={col.title}>
                <h4 className="text-white font-semibold text-sm mb-3">{col.title}</h4>
                <ul className="space-y-1.5">
                  {col.links.map(link => (
                    <li key={link}>
                      <motion.a href="#" className="text-xs opacity-50 hover:opacity-90 hover:text-white transition-all inline-block" whileHover={{ x: 3 }}>{link}</motion.a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t pt-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs opacity-30" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <span>© 2024 长安大学 版权所有</span>
            <span>陕ICP备05001974号 | 公安备案号61010802000041</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
